/**
 * SOLID Review
 * S — Strong. One job: turn a raw MediaDeviceInfo-like list into a headphones/speakers/unknown
 *     classification. The regex heuristics (HEADPHONE_RE/SPEAKER_RE) and the output-then-input fallback
 *     strategy are all in service of that single decision — nothing else is mixed in.
 * O — Good. Extending the platform matrix (e.g. a new label pattern) means editing the regexes or adding a
 *     branch inside classifyAudioRoute; the IEntityAudioDevice/IEntityAudioRouteClassification shapes don't need to
 *     change for that.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two small interfaces (IEntityAudioDevice, IEntityAudioRouteClassification) plus one function; narrow
 *     and cohesive.
 * D — Strong. Zero imports — depends on nothing, not even the DOM lib's MediaDeviceInfo (deliberately
 *     loosely typed via IEntityAudioDevice so callers can pass real MediaDeviceInfo without this file
 *     depending on it).
 */
// ---------------------------------------------------------------------------
// AUDIO ROUTE CLASSIFIER — pure function deciding whether the device is
// playing through headphones, speakers, or something we cannot determine.
//
// Platform matrix this encodes:
//  - Chrome/Edge desktop + Android Chrome expose labeled 'audiooutput'
//    devices once mic permission is granted → classify from outputs.
//  - Firefox (without the speaker-selection permission) and iOS Safari never
//    expose 'audiooutput' → fall back to 'audioinput' labels (BT/wired
//    headsets carry a mic that shows up as a labeled input).
//  - Input-only evidence can prove headphones but never speakers: mic-less
//    wired headphones (common on iOS) create no audioinput, so their absence
//    means nothing → 'unknown', never auto-mute from it.
// ---------------------------------------------------------------------------

export type AudioRoute = 'headphones' | 'speakers' | 'unknown';

export interface IEntityAudioDevice {
  /** MediaDeviceKind, loosely typed so MediaDeviceInfo can be passed directly. */
  readonly kind: string;
  readonly label: string;
}

export interface IEntityAudioRouteClassification {
  readonly route: AudioRoute;
  /** The device label that decided the classification, for debugging/tooltips. */
  readonly matchedLabel?: string;
  readonly source: 'audiooutput' | 'audioinput' | 'none';
}

const HEADPHONE_RE = /headphone|earphone|air ?pods?|headset|earbud|\bbuds?\b|bluetooth|hands.?free/i;
// Guards the "unrecognized external output" heuristic below: anything
// speaker-like, virtual-default-like, or a display audio sink is NOT treated
// as an arbitrarily-named Bluetooth headphone.
const SPEAKER_RE = /speaker|built.?in|internal|earpiece|speakerphone|hdmi|display|monitor|\btv\b|default/i;

export function classifyAudioRoute(
  devices: ReadonlyArray<IEntityAudioDevice>,
): IEntityAudioRouteClassification {
  const outputs = devices.filter((d) => d.kind === 'audiooutput' && d.label.length > 0);
  const inputs = devices.filter((d) => d.kind === 'audioinput' && d.label.length > 0);

  if (outputs.length > 0) {
    const headphoneOutput = outputs.find((d) => HEADPHONE_RE.test(d.label));
    if (headphoneOutput) {
      return { route: 'headphones', matchedLabel: headphoneOutput.label, source: 'audiooutput' };
    }
    // Corroboration via inputs: a BT headset with an arbitrary product name
    // ("WH-1000XM4") often exposes a hands-free/headset-flavoured input.
    const headphoneInput = inputs.find((d) => HEADPHONE_RE.test(d.label));
    if (headphoneInput) {
      return { route: 'headphones', matchedLabel: headphoneInput.label, source: 'audioinput' };
    }
    // Unrecognized external output: labeled, but neither headphone- nor
    // speaker-like — most plausibly a Bluetooth device named after its model.
    const externalOutput = outputs.find((d) => !SPEAKER_RE.test(d.label));
    if (externalOutput) {
      return { route: 'headphones', matchedLabel: externalOutput.label, source: 'audiooutput' };
    }
    return { route: 'speakers', matchedLabel: outputs[0].label, source: 'audiooutput' };
  }

  if (inputs.length > 0) {
    const headphoneInput = inputs.find((d) => HEADPHONE_RE.test(d.label));
    if (headphoneInput) {
      return { route: 'headphones', matchedLabel: headphoneInput.label, source: 'audioinput' };
    }
    return { route: 'unknown', source: 'audioinput' };
  }

  return { route: 'unknown', source: 'none' };
}
